import { ChevronDown, ChevronUp, ChevronsRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useRef } from "react";

const SidebarMenu = ({
  collapsed,
  title,
  icon,
  to,
  expanded,
  onToggle,
  submenuItems = [],
  isFirst = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasSubmenu = submenuItems.length > 0;
  const [hovered, setHovered] = useState(false);
  const timeoutRef = useRef(null);

  const isActive =
    location.pathname === to ||
    submenuItems.some((item) => location.pathname.startsWith(item.to));

  const handleMainClick = () => {
    if (collapsed) {
      navigate(to);
    } else if (hasSubmenu) {
      onToggle();
    } else {
      navigate(to);
    }
  };

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setHovered(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setHovered(false), 150);
  };

  return (
    <div
      className={`relative group ${
        (!collapsed && !isActive) || expanded
          ? "after:absolute after:bottom-0 after:left-3 after:right-3 after:h-px after:border-b after:border-[#1a325d] after:border-dashed"
          : ""
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main menu item */}
      <div
        className={`relative flex items-center justify-between w-full p-2
        ${
          !collapsed && isActive
            ? "bg-gradient-to-r from-[#1a325d] to-[#27ade3]"
            : ""
        }
        ${!collapsed && isActive && isFirst ? "rounded-t-xl" : ""}
        font-semibold transition-all duration-300 group overflow-hidden`}
      >
        {/* Icon + Title link */}
        <Link
          to={to}
          onClick={(e) => {
            if (hasSubmenu) {
              e.preventDefault();
              onToggle();
              if (!expanded) {
                navigate(to);
              }
            }
          }}
          className="flex items-center gap-3 flex-1 z-10 text-inherit no-underline hover:opacity-90"
        >
          {icon && (
            <span
              className={`flex items-center justify-center w-10 h-10 rounded-md transition-all duration-300
                ${
                  collapsed
                    ? isActive
                      ? "bg-[#27ade3] text-white shadow-md"
                      : "text-[#0a2540] hover:bg-[#eef6fb]"
                    : isActive
                    ? "text-white"
                    : "text-[#0a2540]"
                }
              `}
            >
              {icon}
            </span>
          )}

          {!collapsed && (
            <span
              className={`text-base font-normal transition-colors ${
                isActive ? "text-white" : "text-[#0a2540]"
              }`}
            >
              {title}
            </span>
          )}
        </Link>

        {/* Chevron */}
        {!collapsed && hasSubmenu && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle();
            }}
            className="z-20 ml-2 bg-transparent border-none focus:outline-none"
            aria-label="Toggle submenu"
          >
            {expanded ? (
              <ChevronUp
                size={18}
                className={isActive ? "text-white" : "text-[#1a325d]"}
              />
            ) : (
              <ChevronDown
                size={18}
                className={isActive ? "text-white" : "text-[#1a325d]"}
              />
            )}
          </button>
        )}
      </div>

      {/* Submenu */}
      {!collapsed && expanded && (
        <div
          className={`relative p-3 space-y-4 text-sm text-white transition-all overflow-hidden max-h-[500px]`}
        >
          {submenuItems.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`group flex items-center px-3 py-1 text-[15px] transition-all duration-300 ease-in-out
              ${
                location.pathname.startsWith(to)
                  ? "text-[#0a2540]"
                  : "text-[#0179aa] hover:text-[#0a2540] hover:translate-x-[4px]"
              }`}
            >
              <div className="mr-2">
                <ChevronsRight size={16} />
              </div>
              {label}
            </Link>
          ))}
        </div>
      )}

      {/* Floating submenu for collapsed sidebar */}
      {collapsed && hasSubmenu && hovered && (
        <div
          className={`absolute left-full top-2 ml-2 z-20 w-60
            rounded-xl overflow-hidden bg-white/90 backdrop-blur-md border border-[#e2e8f0] shadow-2xl
            transition-all duration-200 ease-out
            transform origin-top-left
            ${
              hovered
                ? "opacity-100 scale-100 translate-x-0"
                : "opacity-0 scale-95 -translate-x-2 pointer-events-none"
            }
          `}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="px-4 pt-3 pb-1">
            <Link to={to} className="no-underline">
              <div className="px-4 pt-3 pb-2 border-b border-gray-200">
                <div className="text-sm font-semibold text-[#0a2540]">
                  {title}
                </div>
              </div>
            </Link>
          </div>

          <div className="py-1">
            {submenuItems.map(({ to, label }) => {
              const isSubActive = location.pathname.startsWith(to);

              return (
                <Link
                  key={to}
                  to={to}
                  className={`block px-4 py-2 font-normal text-sm rounded transition duration-200
                    ${
                      isSubActive
                        ? "text-[#27ade3] bg-[#f1faff]"
                        : "text-[#1a325d] hover:text-[#27ade3] hover:bg-[#f1faff]"
                    }
                  `}
                >
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarMenu;
